import type { TimeTrackEntry, TimeTrackTarget } from "@shared/timetrack";

/** Digits use the final two places as minutes: 130 = 1:30, 45 = 0:45. */
export function parseDuration(input: string): { seconds: number; formatted: string; error?: never } | { error: string; seconds?: never; formatted?: never } {
  const value = input.trim();
  if (!value) return { error: "Enter a duration, for example 1:30 or 45." };
  if ((value.match(/:/g) || []).length > 1) return { error: "Use hours and minutes only (H:MM), without seconds." };
  if (!/^(?:\d+|\d+:\d{2})$/.test(value)) return { error: "Use H:MM or digits only. No negative values, decimals or seconds." };
  const [hours, minutes] = value.includes(":")
    ? value.split(":").map(Number)
    : [Number(value.length > 2 ? value.slice(0, -2) : 0), Number(value.slice(-2))];
  if (minutes > 59) return { error: "Minutes must be between 00 and 59. For one hour, enter 1:00 or 100." };
  const seconds = hours * 3600 + minutes * 60;
  if (!Number.isSafeInteger(seconds) || seconds > 36000000) return { error: "Enter a duration of up to 10000:00." };
  return { seconds, formatted: `${hours}:${String(minutes).padStart(2, "0")}` };
}

export function minuteDuration(seconds: number) {
  return `${Math.floor(seconds / 3600)}:${String(Math.floor(seconds / 60) % 60).padStart(2, "0")}`;
}

export function civilDate(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function dateWindow(date: string, mode: "day" | "week") {
  const start = new Date(`${date}T12:00:00`);
  if (mode === "week") start.setDate(start.getDate() - (start.getDay() + 6) % 7);
  const end = new Date(start);
  if (mode === "week") end.setDate(end.getDate() + 6);
  return { from: civilDate(start), to: civilDate(end) };
}

export function shiftDate(date: string, days: number) {
  const next = new Date(`${date}T12:00:00`);
  next.setDate(next.getDate() + days);
  return civilDate(next);
}

export function exactDuration(seconds: number) {
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 3600)}:${String(Math.floor(total / 60) % 60).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export function summaryDuration(seconds: number) {
  const total = Math.round(seconds / 60);
  return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, "0")}m`;
}

export function entrySeconds(entry: TimeTrackEntry, receivedAt: number, now: number) {
  return entry.durationSeconds + (entry.state === "running" ? Math.max(0, Math.floor((now - receivedAt) / 1000)) : 0);
}

export function needsForeignConfirmation(entry: TimeTrackEntry, userId: number) {
  return entry.workerId !== userId || entry.createdById === null || entry.createdById !== userId;
}

export function targetKey(target: Pick<TimeTrackTarget, "targetType" | "targetId">) {
  return `${target.targetType}:${target.targetId}`;
}

export function targetBreadcrumb(target: TimeTrackTarget, targets: TimeTrackTarget[]) {
  const parts = [target.label];
  let current = target;
  const visited = new Set([targetKey(current)]);
  while (current.parentType && current.parentId) {
    const parent = targets.find(t => t.targetType === current.parentType && t.targetId === current.parentId && t.projectId === current.projectId);
    if (!parent || visited.has(targetKey(parent))) break;
    parts.unshift(parent.label);
    visited.add(targetKey(parent));
    current = parent;
  }
  return parts.join(" / ");
}

/** Follows real parents; a pulse may sit directly under a project or sequence. */
export function belongsToTarget(entry: TimeTrackEntry, target: TimeTrackTarget, targets: TimeTrackTarget[]) {
  if (entry.projectId !== target.projectId) return false;
  if (target.targetType === "project") return true;
  let type = entry.targetType;
  let id = entry.targetId;
  const visited = new Set<string>();
  for (;;) {
    if (type === target.targetType && id === target.targetId) return true;
    const key = `${type}:${id}`;
    if (visited.has(key)) return false;
    visited.add(key);
    const item = targets.find(t => t.targetType === type && t.targetId === id && t.projectId === entry.projectId);
    if (target.targetType === "sequence" && item?.sequenceIds.includes(target.targetId)) return true;
    if (!item?.parentType || !item.parentId) return false;
    type = item.parentType;
    id = item.parentId;
  }
}
